import { FetchCacheItemsSources, FetchItemsGqlQuery } from '../../../../types';
import {
  FetchCacheItemTagsFilters,
  FetchItemTagsCacheKey,
  FetchItemTagsLimit,
  FetchItemTagsPage,
  FetchItemTagsSort
} from '../../itemTagsTypes';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_ITEM_TAGS_FILTERS,
  INITIAL_ITEM_TAGS_LIMIT,
  INITIAL_ITEM_TAGS_PAGE,
  INITIAL_ITEM_TAGS_SORT,
  ItemTagsPermissions
} from '../../itemTagsConstants';

interface CachePaginatedItemTagsOptions {
  action?: string;
  cacheKey: FetchItemTagsCacheKey;
  query: FetchItemsGqlQuery;
  initialPageFilters?: FetchCacheItemTagsFilters;
  initialSort?: FetchItemTagsSort;
  initialPage?: FetchItemTagsPage;
  initialLimit?: FetchItemTagsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
  trackTotalHits?: boolean;
  source?: FetchCacheItemsSources;
}

const cacheScope = 'itemTags';

function useCachePaginatedItemTags<FetchItemTagsItemTagType>({
  action = ItemTagsPermissions.READ_ITEM_TAGS_INDEX_PAGE,
  cacheKey,
  query,
  initialPageFilters = INITIAL_ITEM_TAGS_FILTERS,
  initialSort = INITIAL_ITEM_TAGS_SORT,
  initialPage = INITIAL_ITEM_TAGS_PAGE,
  initialLimit = INITIAL_ITEM_TAGS_LIMIT,
  options = {},
  trackTotalHits,
  source = FetchCacheItemsSources.DB
}: CachePaginatedItemTagsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    changeItemsFilters,
    clearItemsFilters,
    filterItems,
    clearItemsFiltersPersistInitial,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    updateItemCache,
    prefetchItem,
    fetchPreparedData,
    prefetchData,
    getCachedData,
    subscribeInvalidationEvents,
    unsubscribeInvalidationEvents
  } = useCacheIndexQuery<FetchItemTagsItemTagType>({
    action,
    defaultApi: 'cache_api',
    scope: cacheScope,
    query,
    cacheQuery: query,
    cacheKey,
    initialFilters: initialPageFilters,
    initialLimit,
    initialPage,
    initialSort,
    options,
    trackTotalHits,
    source
  });

  return {
    itemTagsData: data,
    itemTags: items,
    itemTagsError: itemsError,
    itemTagsTotalCount: itemsTotalCount,
    itemTagsFetched: isFetched,
    itemTagsLoading: isLoading,
    itemTagsIsPlaceholderData: isPlaceholderData,
    itemTagsFilters: currentFilters,
    itemTagsSort: currentSort,
    itemTagsPage: currentPage,
    itemTagsLimit: currentLimit,
    filterItemTags: filterItems,
    clearItemTagsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    changeItemTagsFilters: changeItemsFilters,
    clearItemTagsFilters: clearItemsFilters,
    sortItemTags: sortItems,
    paginateItemTags: paginateItems,
    limitItemTags: limitItems,
    prefetchItemTags: prefetchItems,
    prefetchItemTag: prefetchItem,
    updateItemTagCache: updateItemCache,
    fetchItemTagsPreparedData: fetchPreparedData,
    prefetchItemTagsData: prefetchData,
    getItemTagsCachedData: getCachedData,
    subscribeItemTagsInvalidationEvents: subscribeInvalidationEvents,
    unsubscribeItemTagsInvalidationEvents: unsubscribeInvalidationEvents
  };
}

export default useCachePaginatedItemTags;
